import { useCallback, useEffect, useMemo, useRef } from "react";
import { useFormContext } from "react-hook-form";
import { WorksheetItemInput } from "DDD/action-objects/WorksheetItemUpdate";
import { HelperResolution, convertQuantity } from "utils/worksheet/worksheetHelpers";
import { WorksheetCostCategory } from "generated/graphql";

export type CostRowLinkSnapshot = {
  quantitySource: string;
  quantityOverridden?: boolean;
};

const isHourlyCostRow = (row: WorksheetItemInput["costRows"][number]) =>
  [WorksheetCostCategory.LABOR, WorksheetCostCategory.EQUIPMENT].includes(row.category) &&
  String(row.unit ?? "").toLowerCase() === "hours";

export function useCalculationsDrawerData(values: WorksheetItemInput) {
  const materialRows = useMemo(
    () => (values.costRows ?? []).filter((row) => row.category === WorksheetCostCategory.MATERIAL),
    [values.costRows]
  );

  const materialOptions = useMemo(
    () =>
      materialRows.map((row) => ({
        value: row.rowUuid,
        label: row.name || "Material",
      })),
    [materialRows]
  );

  const hourlyRows = useMemo(
    () =>
      (values.costRows ?? [])
        .map((row, index) => ({ row, index }))
        .filter(({ row }) => isHourlyCostRow(row)),
    [values.costRows]
  );

  const selectedMaterial = useMemo(
    () => materialRows.find((row) => row.rowUuid === values.helpers?.production?.materialRowUuid),
    [materialRows, values.helpers?.production?.materialRowUuid]
  );

  const productionUnit = useMemo(
    () =>
      values.helpers?.production?.source === "material"
        ? selectedMaterial?.unit ?? ""
        : values.unit,
    [values.helpers?.production?.source, selectedMaterial?.unit, values.unit]
  );

  return { materialRows, materialOptions, hourlyRows, selectedMaterial, productionUnit };
}

export function useCalculationsSnapshot(open: boolean, onClose: () => void) {
  const { getValues, setValue } = useFormContext<WorksheetItemInput>();
  const snapshot = useRef<{
    helpers: WorksheetItemInput["helpers"];
    links: CostRowLinkSnapshot[];
  } | null>(null);

  useEffect(() => {
    if (!open) return;

    const current = getValues();

    snapshot.current = {
      helpers: current.helpers ? JSON.parse(JSON.stringify(current.helpers)) : current.helpers,
      links: (current.costRows ?? []).map((row) => ({
        quantitySource: row.quantitySource,
        quantityOverridden: row.quantityOverridden,
      })),
    };
  }, [open, getValues]);

  const handleCancel = useCallback(() => {
    const saved = snapshot.current;

    if (saved) {
      setValue("helpers", saved.helpers, { shouldDirty: false });
      saved.links.forEach((link, index) => {
        setValue(`costRows.${index}.quantitySource`, link.quantitySource, { shouldDirty: false });
        setValue(`costRows.${index}.quantityOverridden`, link.quantityOverridden, {
          shouldDirty: false,
        });
      });
    }

    snapshot.current = null;
    onClose();
  }, [onClose, setValue]);

  return handleCancel;
}

export function useProductionFormSync(
  values: WorksheetItemInput,
  productionUnit: string,
  resolution: HelperResolution
) {
  const { setValue } = useFormContext<WorksheetItemInput>();
  const production = values.helpers?.production;

  useEffect(() => {
    const enabled = Number(production?.rate ?? "") > 0;

    if (production?.enabled !== enabled) {
      setValue("helpers.production.enabled", enabled, { shouldDirty: false });
    }
  }, [production?.rate, production?.enabled]);

  useEffect(() => {
    if (!productionUnit || production?.unit === productionUnit) return;

    let nextRate = production?.rate ?? "";

    if (nextRate) {
      const converted = convertQuantity(
        nextRate,
        production?.unit || productionUnit,
        productionUnit
      );

      nextRate = converted ? converted.toDecimalPlaces(8).toString() : "";
    }

    setValue("helpers.production.unit", productionUnit, { shouldDirty: false });
    setValue("helpers.production.rate", nextRate, { shouldDirty: false });
  }, [productionUnit, production?.unit]);

  return resolution;
}

export function useCostRowLinkToggles(values: WorksheetItemInput) {
  const { setValue } = useFormContext<WorksheetItemInput>();

  return useCallback(
    (index: number, kind: "duration" | "travel", enabled: boolean) => {
      const row = values.costRows?.[index];

      if (!row) return;

      setValue(`costRows.${index}.quantitySource`, linkTarget(row.quantitySource, kind, enabled), {
        shouldDirty: true,
      });
      setValue(`costRows.${index}.quantityOverridden`, false, { shouldDirty: true });
    },
    [values.costRows, setValue]
  );
}

const linkTarget = (
  current: string | null | undefined,
  kind: "duration" | "travel",
  enabled: boolean
) => {
  const flags = {
    duration: (current ?? "").includes("duration"),
    travel: (current ?? "").includes("travel"),
  };

  flags[kind] = enabled;

  if (flags.duration && flags.travel) return "duration_travel";
  if (flags.duration) return "duration";

  return flags.travel ? "travel" : "manual";
};
